import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PartsSearchPage from './pages/PartsSearchPage';
import TemplatesPage from './pages/TemplatesPage';
import CostCardCreatePage from './pages/CostCardCreatePage';
import CostCardsPage from './pages/CostCardsPage';
import CostCardDetailPage from './pages/CostCardDetailPage';
import MyTasksPage from './pages/MyTasksPage';
import KanbanPage from './pages/KanbanPage';
import SuperSeededPage from './pages/SuperSeededPage';
import InsightsPage from './pages/InsightsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AdminPage from './pages/AdminPage';
import HelpSupportPage from './pages/HelpSupportPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 dark:bg-[#09090b] flex items-center justify-center transition-colors">
        <div className="animate-spin w-8 h-8 border-2 border-[#C3002F] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 dark:bg-[#09090b] flex items-center justify-center transition-colors">
        <div className="animate-spin w-8 h-8 border-2 border-[#C3002F] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parts"
        element={
          <ProtectedRoute>
            <PartsSearchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <TemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cost-cards"
        element={
          <ProtectedRoute>
            <CostCardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cogs-cards"
        element={
          <ProtectedRoute>
            <CostCardsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cost-cards/create"
        element={
          <ProtectedRoute>
            <CostCardCreatePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cogs-cards/create"
        element={
          <ProtectedRoute>
            <CostCardCreatePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cost-cards/:id"
        element={
          <ProtectedRoute>
            <CostCardDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cogs-cards/:id"
        element={
          <ProtectedRoute>
            <CostCardDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-tasks"
        element={
          <ProtectedRoute>
            <MyTasksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kanban"
        element={
          <ProtectedRoute>
            <KanbanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/super-seeded"
        element={
          <ProtectedRoute>
            <SuperSeededPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insights"
        element={
          <ProtectedRoute>
            <InsightsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpSupportPage />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Theme-aware Toaster component
const ThemedToaster = () => {
  const { isDark } = useTheme();
  
  return (
    <Toaster 
      position="top-right"
      toastOptions={{
        style: isDark ? {
          background: '#18181b',
          border: '1px solid #27272a',
          color: '#fafafa'
        } : {
          background: '#ffffff',
          border: '1px solid #e4e4e7',
          color: '#09090b'
        }
      }}
    />
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
